import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MeTeam } from '@scio3/shared-types';
import { ROLE_TYPES, ROOT_TEAM_ID } from '@scio3/shared-types';
import { useFavoritesStore } from '@/stores/favorites-store';
import { useAuthStore } from '@/stores/auth-store';
// The All-view flag + its scope sentinel live with the feature (one place to flip, per Ted's
// 2026-08-26 decision), so the store reads them rather than redeclaring them.
import { ALL_TEAMS_SCOPE, SHOW_ALL_VIEW } from '@/features/group-security/constants';

interface TeamState {
  teams: MeTeam[];
  activeTeamId: string;
  /** Whether the active team is the root/default team */
  isDefaultTeam: boolean;
  /**
   * List SCOPE (Group Security §1, T901): `ALL_TEAMS_SCOPE` or a teamId.
   *
   * Separate from `activeTeamId` on purpose — `activeTeamId` is the AUTH team (the top-level
   * `teamId` GraphQL arg, which must always be a real teamspace the user belongs to), while this is
   * the optional `query.teamId` FILTER on top of it. Picking a team sets both; picking All changes
   * only this one.
   *
   * AV-1: the default is the current team, never All — `clearTeams()` (logout) resets it, so a
   * fresh login always lands on currentTeam.
   */
  teamFilter: string;
  /**
   * May this user use the [ALL] teamspaces scope at all? OWNER / SUPER_ADMIN only
   * (Ted, 2026-09-09 — this REVERSES the 2026-08-26 "no role gate" decision).
   *
   * Captured on `setTeams` rather than read live, because the three scope selectors below are pure
   * over this store and every list hook reads them — putting the gate anywhere else risks one call
   * site keeping the `'all'` sentinel on the wire while the UI hides the entry. `setMe` runs
   * immediately before `setTeams` at all 7 call sites, so the role is always current here, and an
   * absent `me` yields `false` — it fails CLOSED.
   */
  canUseAllView: boolean;

  setTeams: (teams: MeTeam[]) => void;
  setActiveTeam: (teamId: string) => void;
  setTeamFilter: (teamFilter: string) => void;
  getActiveTeam: () => MeTeam | undefined;
  clearTeams: () => void;
}

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      teams: [],
      activeTeamId: '',
      isDefaultTeam: true,
      teamFilter: '',
      canUseAllView: false,

      setTeams: (teams) => {
        const current = get().activeTeamId;
        const isValid = current && teams.some((t) => t._id === current);
        const newTeamId = isValid ? current : (teams[0]?._id ?? '');
        // Reconcile a stale persisted filter: a teamId that is no longer in the user's reach falls
        // back to the resolved active team. `ALL_TEAMS_SCOPE` is preserved — setTeams also runs on
        // an in-session ME refetch, so resetting it here would silently kick the user out of All.
        // Role gate for [ALL] (Ted, 2026-09-09): OWNER / SUPER_ADMIN only. `setMe` runs immediately
        // before every `setTeams` call site, so `me` is current; an absent `me` yields false, which
        // fails CLOSED (the scope is hidden, never wrongly offered).
        const rootRole = useAuthStore.getState().me?.rootRole;
        const canUseAllView = rootRole === ROLE_TYPES.OWNER || rootRole === ROLE_TYPES.SUPER_ADMIN;
        const filter = get().teamFilter;
        // `ALL_TEAMS_SCOPE` is preserved across an in-session ME refetch — resetting it would kick the
        // user out of All — but ONLY while they may still use it. A user who no longer qualifies (role
        // changed, or a stale persisted filter from another account) is reconciled back to a real
        // teamspace, so the sentinel cannot linger in a store it is no longer allowed to hold.
        const isFilterValid =
          filter === ALL_TEAMS_SCOPE
            ? canUseAllView
            : !!filter && teams.some((t) => t._id === filter);
        set({
          teams,
          canUseAllView,
          ...(isValid ? {} : { activeTeamId: newTeamId }),
          isDefaultTeam: !newTeamId || newTeamId === ROOT_TEAM_ID,
          ...(isFilterValid ? {} : { teamFilter: newTeamId }),
        });
      },
      setActiveTeam: (teamId) => {
        // Favorites are per-(user, team) — wipe on an actual team change so the previous team's
        // stars don't linger until the new team's favorites query reconciles.
        if (teamId !== get().activeTeamId) {
          useFavoritesStore.getState().reset();
        }
        set({
          activeTeamId: teamId,
          isDefaultTeam: !teamId || teamId === ROOT_TEAM_ID,
          // Picking a teamspace leaves the All scope and filters to that team.
          teamFilter: teamId,
        });
      },
      // Entering All keeps `activeTeamId` (the auth team) untouched — see `teamFilter` above.
      setTeamFilter: (teamFilter) => set({ teamFilter }),
      getActiveTeam: () => get().teams.find((t) => t._id === get().activeTeamId),
      clearTeams: () => set({ teams: [], activeTeamId: '', teamFilter: '', canUseAllView: false }),
    }),
    { name: 'scio3-team' },
  ),
);

type TeamScopeState = Pick<TeamState, 'teams' | 'activeTeamId' | 'teamFilter' | 'canUseAllView'>;

/**
 * Is the [ALL] view offered to this user at all? THREE conditions:
 *   1. `SHOW_ALL_VIEW` — the one-line kill switch for the whole feature.
 *   2. `canUseAllView` — OWNER / SUPER_ADMIN only (Ted, 2026-09-09). This **reverses** the
 *      2026-08-26 "show it to EVERYONE, no role gate" decision recorded in
 *      `claude-dev/ted/2026-07-23-group-security/24-spec-0824-decided.md` §1.
 *   3. reach > 1 teamspace — with a single teamspace, All and that teamspace are the same list, so
 *      the entry is noise.
 *
 * Every scope selector below funnels through this, so the role gate reaches the wire (`teamFilter`)
 * and not just the picker — a UI-only gate would still let a non-owner's list send `'all'`.
 */
export function selectIsAllViewAvailable(state: TeamScopeState): boolean {
  return SHOW_ALL_VIEW && state.canUseAllView && state.teams.length > 1;
}

/** Is the user currently scoped to All? False whenever the view isn't available to them. */
export function selectIsAllTeamsScope(state: TeamScopeState): boolean {
  return selectIsAllViewAvailable(state) && state.teamFilter === ALL_TEAMS_SCOPE;
}

/**
 * The `query.teamId` filter a ROOT-level list should send. THREE explicit states — this selector is
 * the single decision point every list hook reads (T903):
 *
 *  1. All scope        ⇒ `ALL_TEAMS_SCOPE` — the sentinel is sent EXPLICITLY (Ted, 2026-08-26:
 *     "we should create filter teamId = 'all'"). The backend normalizes it away before building the
 *     query, so the reach is the same full cross-team reach as omitting the field. Sending it
 *     beats omitting it because "no filter" is then unambiguous: a real teamId AND-scopes the list
 *     (that narrowing is the whole point of the picker), so "field absent" can keep meaning
 *     "back-compat client that never heard of the scope" instead of doubling as "All".
 *  2. Teamspace scope  ⇒ the auth team's id — narrows the list to rows OWNED by that teamspace,
 *     the same value the Workspace column shows, so filter and column always agree.
 *  3. Single-teamspace reach (or no team resolved yet) ⇒ `undefined`, so the field is OMITTED and
 *     those accounts keep sending byte-identical variables: no new Apollo cache key, no extra
 *     fetch, no behavior change from before the All view existed.
 *
 * Only state 1 is ROLE-gated. #2324: gating state 2 on `canUseAllView` too meant every non-owner
 * with multi-teamspace reach fell through to `undefined` — and the backend reads an omitted
 * `query.teamId` as the SAME full cross-team reach as the `'all'` sentinel (`group.types.ts`), so
 * the gate that exists to withhold All was handing it out. A user who may not use All still
 * narrows to the teamspace they are standing in.
 *
 * `ALL_TEAMS_SCOPE` must never be used as the top-level `teamId` GraphQL ARG (the AUTH team, which
 * is membership-validated) — only ever inside the `query` / `input` FILTER object.
 */
export function selectListScopeTeamId(state: TeamScopeState): string | undefined {
  // No narrowing to do, and never send an empty string: the backend treats `''` as a REAL filter
  // (`typeof === 'string'` and not the sentinel), which exact-matches no folder and empties the list.
  if (state.teams.length <= 1 || !state.activeTeamId) return undefined;
  if (state.teamFilter === ALL_TEAMS_SCOPE && selectIsAllViewAvailable(state)) {
    return ALL_TEAMS_SCOPE;
  }
  return state.activeTeamId;
}

/**
 * EVERY teamspace this user belongs to — the frontend's equivalent of the backend's reach.
 *
 * This is the correct `teamIds` argument for `resolveFolderAccess`. Passing only `activeTeamId`
 * there is a BUG: the backend authorizes a folder against all of my teamspaces (v4 D2 — my
 * teamspaces ∪ shared-to-me ∪ Everyone-in-account), so a folder living in another teamspace I am a
 * member of is served by the server and would then be refused by the client. That mismatch is what
 * made clicking a cross-teamspace folder in All view bounce to Home with "You do not have access to
 * this folder" (#GS-allview). Group grants come from `me.groupIds`, which the resolver takes
 * separately — this covers the teamspace-membership half only.
 *
 * Returns the store's own `teams` mapping; callers that need referential stability across renders
 * should use `useMyTeamIds()` below rather than calling this in a zustand selector.
 */
export function selectMyTeamIds(state: TeamScopeState): string[] {
  return state.teams.map((t) => t._id);
}

/**
 * Referentially-stable `selectMyTeamIds` for use in render / `useMemo` deps.
 *
 * `teams` is a stable reference from the store, so the mapped array is only rebuilt when the team
 * list actually changes. Selecting the mapped array directly from zustand would allocate a new
 * array on every store read and re-render forever.
 */
export function useMyTeamIds(): string[] {
  const teams = useTeamStore((s) => s.teams);
  return useMemo(() => teams.map((t) => t._id), [teams]);
}
